import { Link } from 'react-router-dom';
import { ArrowRight, FolderKanban, Star, AlertCircle } from 'lucide-react';
import { useMyProjects } from '../../hooks/useMyHome';
import type { MyProject } from '../../hooks/useMyHome';
import Spinner from '../ui/Spinner';

const TYPE_LABEL: Record<string, string> = {
  SCRUM: '스크럼',
  KANBAN: '칸반',
  CS: 'CS',
  TEAM: '팀 작업',
};

const TYPE_BADGE_COLORS: Record<string, string> = {
  SCRUM: 'bg-blue-100 text-blue-800',
  KANBAN: 'bg-green-100 text-green-800',
  CS: 'bg-purple-100 text-purple-800',
  TEAM: 'bg-orange-100 text-orange-800',
};

const ROLE_LABEL: Record<string, string> = {
  PROJECT_MANAGER: 'PM',
  PROJECT_LEADER: 'PL',
  MEMBER: '멤버',
  STAKEHOLDER: '이해관계자',
};

function ProjectCard({ project }: { project: MyProject }) {
  return (
    <Link
      to={`/projects/${project.key}`}
      className="flex-shrink-0 w-56 bg-white border border-gray-200 rounded-lg p-4 hover:border-primary-300 hover:shadow-sm transition-all"
    >
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="min-w-0">
          <h4 className="text-sm font-medium text-gray-900 truncate">{project.name}</h4>
          <span className="text-sm text-gray-400">{project.key}</span>
        </div>
        {project.isFavorite && (
          <Star className="size-3.5 text-yellow-500 fill-yellow-500 shrink-0 mt-0.5" />
        )}
      </div>

      <div className="flex items-center gap-1.5 mb-3">
        <span className={`text-[11px] font-medium px-1.5 py-0.5 rounded ${TYPE_BADGE_COLORS[project.projectType] ?? 'bg-gray-100 text-gray-700'}`}>
          {TYPE_LABEL[project.projectType] ?? project.projectType}
        </span>
        <span className="text-[11px] text-gray-500 bg-gray-50 px-1.5 py-0.5 rounded">
          {ROLE_LABEL[project.myRole] ?? project.myRole}
        </span>
      </div>

      {project.myOpenIssueCount > 0 && (
        <div className="flex items-center gap-1 text-sm text-orange-600">
          <AlertCircle className="size-3" />
          <span>미해결 이슈 {project.myOpenIssueCount}건</span>
        </div>
      )}
    </Link>
  );
}

export default function ProjectCards() {
  const { data: projects, isLoading } = useMyProjects();

  if (isLoading) return <Spinner />;

  if (!projects || projects.length === 0) {
    return (
      <div className="flex items-center gap-2 py-4 text-sm text-gray-400">
        <FolderKanban className="size-4" />
        <span>참여 중인 프로젝트가 없습니다</span>
      </div>
    );
  }

  return (
    <div>
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1">
        {projects.map((project) => (
          <ProjectCard key={project.projectId} project={project} />
        ))}
      </div>
      {projects.length >= 6 && (
        <Link to="/projects" className="inline-flex items-center gap-1 mt-3 text-sm text-primary-600 hover:text-primary-700 font-medium">
          더보기 <ArrowRight className="size-3" />
        </Link>
      )}
    </div>
  );
}
